import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, EMPTY } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { indicate } from 'src/app/operators';
import { ComponentViewModel, EsraPipingAPIClient } from 'src/app/shared/models/autogenerated-piping';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getApiErrorMessage } from 'src/app/shared/utils';

export interface ReplaceComponentDialogData {
  /** Display name of the inactive component being replaced. */
  inactiveDescription: string;
  /** Release ID used when navigating context (not used in this dialog directly). */
  releaseId: string;
}

export interface ReplaceComponentDialogResult {
  /** ID of the component chosen as the replacement. */
  replacementComponentId: string;
}

@Component({
  selector: 'app-replace-component-dialog',
  templateUrl: './replace-component-dialog.component.html',
  styleUrls: ['./replace-component-dialog.component.scss'],
  standalone: false,
})
export class ReplaceComponentDialogComponent implements OnInit {
  // ── Dialog inputs ─────────────────────────────────────────────────────
  readonly data: ReplaceComponentDialogData = inject(MAT_DIALOG_DATA);

  // ── State ─────────────────────────────────────────────────────────────
  searchText: string = '';
  searchResults: ComponentViewModel[] = [];
  selectedComponent: ComponentViewModel | null = null;

  loadingComponents$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // ── Dependencies ──────────────────────────────────────────────────────
  private readonly apiClient: EsraPipingAPIClient = inject(EsraPipingAPIClient);
  private readonly snackBar: SnackBarService = inject(SnackBarService);
  private readonly dialogRef: MatDialogRef<ReplaceComponentDialogComponent, ReplaceComponentDialogResult> =
    inject(MatDialogRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /** BehaviorSubject that drives the search when the user types. */
  private readonly searchInput$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  ngOnInit(): void {
    // Trigger initial empty-string search to populate the dropdown with all active components.
    this.searchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        this.searchResults = [];
        return this.apiClient.getAllComponentsPaged(0, 50, query || undefined).pipe(
          indicate(this.loadingComponents$),
          catchError(err => {
            this.snackBar.showError(getApiErrorMessage(err, 'Failed to load components.'));
            return EMPTY;
          }),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(response => {
      this.searchResults = this.filterInactiveDuplicateResults(response.results ?? []);
    });
  }

  onSearchTextChange(text: string): void {
    this.searchText = text;
    this.searchInput$.next(text);
  }

  selectComponent(component: ComponentViewModel): void {
    this.selectedComponent = component;
  }

  confirm(): void {
    if (!this.selectedComponent?.componentId) { return; }
    this.dialogRef.close({ replacementComponentId: this.selectedComponent.componentId });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  /** Display function used by mat-autocomplete to show the component code. */
  displayComponent(component: ComponentViewModel | null): string {
    if (!component) { return ''; }
    return `${component.code ?? ''} — ${component.description ?? ''}`;
  }

  private filterInactiveDuplicateResults(results: ComponentViewModel[]): ComponentViewModel[] {
    const activeKeys: Set<string> = new Set<string>();
    results.forEach((component: ComponentViewModel) => {
      if (component.isActive !== false) {
        activeKeys.add(this.buildDuplicateComponentKey(component.group, component.type, component.code));
      }
    });

    return results.filter((component: ComponentViewModel) => {
      if (component.isActive !== false) {
        return true;
      }
      const componentKey: string = this.buildDuplicateComponentKey(component.group, component.type, component.code);
      return !activeKeys.has(componentKey);
    });
  }

  private buildDuplicateComponentKey(group: string | undefined, type: string | undefined, code: string | undefined): string {
    const normalizedGroup: string = group?.trim().toLowerCase() ?? '';
    const normalizedType: string = type?.trim().toLowerCase() ?? '';
    const normalizedCode: string = code?.trim().toLowerCase() ?? '';
    return `${normalizedGroup}|${normalizedType}|${normalizedCode}`;
  }
}
